import styled from '@emotion/styled'
import { ExtranetUserDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableRow,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButtonBase,
  PharmacColumn,
  PharmacIcon,
  PharmacTableColumn,
  PharmacText,
} from '@pharmac/ui'
import { useNavigate } from 'react-router-dom'
import { useExtranetPath } from '../../router/useExtranetPath'
import { CustomerUserListDetails } from './CustomerUserListDetails'

type CustomerUserListTableProps = {
  customerUsers: ExtranetUserDto[]
}

const IconColumn = styled(ExtranetTableColumn)`
  display: flex;
  justify-content: center;
  width: 100%;
`

export const CustomerUserListTable = ({ customerUsers }: CustomerUserListTableProps) => {
  const { t } = useLanguage(['extranet', 'common'])
  const navigate = useNavigate()
  const createLink = useExtranetPath()

  const sortOrder = (a: ExtranetUserDto, b: ExtranetUserDto) => {
    const collator = new Intl.Collator(['fi', 'sv'], { numeric: true, sensitivity: 'base' })
    const nameDiff = collator.compare(a.lastName, b.lastName)

    if (nameDiff !== 0) return nameDiff

    if (a.lastName > b.lastName) return -1
    if (b.lastName > a.lastName) return 1

    return 0
  }

  const sortedByLastname = customerUsers.sort(sortOrder)

  return (
    <>
      <PharmacColumn paddingLargeTop>
        <ExtranetTable>
          <ExtranetTableRow
            style={{
              backgroundColor: 'var(--color-accent-4)',
              border: 'none',
              fontWeight: 600,
            }}
          >
            <ExtranetTableColumn
              paddingMediumHorizontal
              size={0.7}
            >
              {t('lastName', { ns: 'common' })}
            </ExtranetTableColumn>
            <ExtranetTableColumn size={0.5}>{t('firstName', { ns: 'common' })}</ExtranetTableColumn>
            <ExtranetTableColumn size={0.5}>{t('userName', { ns: 'common' })}</ExtranetTableColumn>
            <ExtranetTableColumn>{t('emailAddress', { ns: 'common' })}</ExtranetTableColumn>
            <IconColumn size={0.3}>{t('edit', { ns: 'common' })}</IconColumn>
            <IconColumn size={0.3}>{t('moreInfo', { ns: 'common' })}</IconColumn>
          </ExtranetTableRow>

          {sortedByLastname?.map((a: any) => (
            <ExtranetTableRow
              key={a.id}
              style={{
                backgroundColor: 'var(--color-accent-1)',
              }}
              expand={
                <ExtranetTableRow
                  noBorders
                  noMargin
                  style={{
                    backgroundColor: 'var(--color-accent-1)',
                    borderTop: '1px solid var(--color-accent-4)',
                  }}
                >
                  <ExtranetTableColumn
                    style={{
                      minWidth: 20,
                      maxWidth: 20,
                    }}
                  />
                  <CustomerUserListDetails customerUser={a} />
                </ExtranetTableRow>
              }
              toggleExpand={() => null}
              children={({ isExpanded }) => (
                <>
                  <PharmacTableColumn size={0.7}>
                    <PharmacText>{a.lastName}</PharmacText>
                  </PharmacTableColumn>
                  <PharmacTableColumn size={0.5}>
                    <PharmacText>{a.firstName}</PharmacText>
                  </PharmacTableColumn>
                  <PharmacTableColumn size={0.5}>
                    <PharmacText>{a.userName}</PharmacText>
                  </PharmacTableColumn>
                  <PharmacTableColumn>{a.emailAddress}</PharmacTableColumn>
                  <IconColumn size={0.3}>
                    <PharmacButtonBase
                      onClick={() =>
                        navigate(createLink('oma-annos.customership.user', { id: a.id }))
                      }
                    >
                      <PharmacIcon
                        stroke="purple"
                        icon="edit3"
                      />
                    </PharmacButtonBase>
                  </IconColumn>
                  <IconColumn size={0.3}>
                    {isExpanded ? (
                      <PharmacIcon
                        stroke="purple"
                        icon="chevronUp"
                      />
                    ) : (
                      <PharmacIcon
                        stroke="purple"
                        icon="chevronDown"
                      />
                    )}
                  </IconColumn>
                </>
              )}
            />
          ))}
        </ExtranetTable>
      </PharmacColumn>
    </>
  )
}
